import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  updateNotificationSettings,
  updateCurrentTemplate
} from '../notificationSlice';
import { notificationAPI } from '../notificationAPI';

const NotificationSettingsPanel = () => {
  const dispatch = useDispatch();
  const { currentSettings, loading, error, selectedLevel } = useSelector(state => state.notification);
  const [hasChanges, setHasChanges] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // 템플릿 로드
  const loadTemplates = async () => {
    setLoadingTemplates(true);
    try {
      console.log('=== 알림 설정: 센드온과 동기화 시작 ===');
      
      // 센드온과 동기화 먼저 수행
      console.log('1. 센드온 동기화 API 호출 중...');
      const syncResponse = await notificationAPI.syncTemplatesFromSendon();
      console.log('센드온 동기화 응답:', syncResponse);
      
      // 동기화 후 최신 템플릿 목록 가져오기
      console.log('2. 최신 템플릿 목록 조회 중...');
      const response = await notificationAPI.getAlimtalkTemplates();
      const data = response.data || response;
      setTemplates(Array.isArray(data) ? data : []);
      
      // 동기화 결과 로깅
      if (syncResponse && syncResponse.message) {
        console.log(`동기화 성공: ${syncResponse.message}`);
      }
      
      console.log(`총 ${Array.isArray(data) ? data.length : 0}개 템플릿을 로드했습니다.`);
      
    } catch (error) {
      console.error('센드온 동기화 실패:', error);
      const errorMsg = error.response?.data?.error || error.message || '동기화 중 오류가 발생했습니다.';
      
      // 동기화 실패시에도 기존 템플릿 로드 시도
      try {
        console.log('3. 동기화 실패, 기존 템플릿만 로드 시도...');
        const response = await notificationAPI.getAlimtalkTemplates();
        const data = response.data || response;
        setTemplates(Array.isArray(data) ? data : []);
        
        console.log(`동기화 실패 (${errorMsg}), 하지만 기존 템플릿 ${Array.isArray(data) ? data.length : 0}개 로드됨`);
        
      } catch (fallbackError) {
        console.error('기존 템플릿 로드도 실패:', fallbackError);
        setTemplates([]);
        
        // 콘솔에만 로그 출력
        console.error(`템플릿 동기화 실패: ${errorMsg}`);
      }
    } finally {
      setLoadingTemplates(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, [selectedLevel]);

  const notificationTypes = [
    {
      key: 'assignment_created',
      title: '과제 배정 시 알림',
      description: '새로운 과제가 배정될 때 학생과 학부모에게 발송',
      enabledField: 'assignment_created_enabled',
      templateField: 'assignment_created_template',
      recipientOptions: [
        {
          field: 'assignment_created_send_to_student',
          title: '학생에게 알림톡 보내기',
          description: '비활성화 시 학생에게 알림톡이 발송되지 않습니다.',
          audienceLabel: '학생',
          defaultValue: true
        },
        {
          field: 'assignment_created_send_to_parent',
          title: '학부모에게 알림톡 보내기',
          description: '비활성화 시 학부모에게 알림톡이 발송되지 않습니다.',
          audienceLabel: '학부모',
          defaultValue: true
        }
      ],
      subOptions: [
        {
          field: 'send_retry_assignment_notification',
          title: '복습 과제 포함',
          description: '복습 과제 배정 시에도 알림 발송',
          defaultValue: false
        }
      ]
    },
    {
      key: 'completion_notification',
      title: '과제 완료 시 알림',
      description: '과제 완료 시 학부모에게 발송',
      enabledField: 'completion_notification_enabled',
      templateField: 'completion_notification_template',
      recipientOptions: [
        {
          field: 'completion_notification_send_to_student',
          title: '학생에게 알림톡 보내기',
          description: '활성화 시 학생에게도 완료 알림이 발송됩니다.',
          audienceLabel: '학생',
          defaultValue: false
        },
        {
          field: 'completion_notification_send_to_parent',
          title: '학부모에게 알림톡 보내기',
          description: '비활성화 시 학부모에게 알림톡이 발송되지 않습니다.',
          audienceLabel: '학부모',
          defaultValue: true
        }
      ]
    },
    {
      key: 'dday_3_notification',
      title: '마감일 3일 전 알림',
      description: '과제 마감 3일 전 학생과 학부모에게 발송할 수 있습니다.',
      enabledField: 'dday_3_notification_enabled',
      templateField: 'dday_3_notification_template',
      recipientOptions: [
        {
          field: 'dday_3_notification_send_to_student',
          title: '학생에게 알림톡 보내기',
          description: '비활성화 시 학생에게 3일 전 디데이 알림이 발송되지 않습니다.',
          audienceLabel: '학생',
          defaultValue: true
        },
        {
          field: 'dday_3_notification_send_to_parent',
          title: '학부모에게 알림톡 보내기',
          description: '활성화 시 학부모에게도 3일 전 디데이 알림을 발송합니다.',
          audienceLabel: '학부모',
          defaultValue: false
        }
      ]
    },
    {
      key: 'dday_1_notification',
      title: '마감일 1일 전 알림',
      description: '과제 마감 1일 전 학생과 학부모에게 발송할 수 있습니다.',
      enabledField: 'dday_1_notification_enabled',
      templateField: 'dday_1_notification_template',
      recipientOptions: [
        {
          field: 'dday_1_notification_send_to_student',
          title: '학생에게 알림톡 보내기',
          description: '비활성화 시 학생에게 1일 전 디데이 알림이 발송되지 않습니다.',
          audienceLabel: '학생',
          defaultValue: true
        },
        {
          field: 'dday_1_notification_send_to_parent',
          title: '학부모에게 알림톡 보내기',
          description: '활성화 시 학부모에게도 1일 전 디데이 알림을 발송합니다.',
          audienceLabel: '학부모',
          defaultValue: false
        }
      ]
    },
    {
      key: 'overdue_notification',
      title: '마감일 지난 과제 알림',
      description: '마감일이 지난 미완료 과제가 있는 경우 알림을 발송할 수 있습니다.',
      enabledField: 'overdue_notification_enabled',
      templateField: 'overdue_notification_template',
      recipientOptions: [
        {
          field: 'overdue_notification_send_to_student',
          title: '학생에게 알림톡 보내기',
          description: '비활성화 시 학생에게 만료 과제 알림이 발송되지 않습니다.',
          audienceLabel: '학생',
          defaultValue: true
        },
        {
          field: 'overdue_notification_send_to_parent',
          title: '학부모에게 알림톡 보내기',
          description: '활성화 시 학부모에게도 만료 과제 알림을 발송합니다.',
          audienceLabel: '학부모',
          defaultValue: false
        }
      ]
    }
  ];

  const getToggleValue = (field, fallback = false) => {
    if (!currentSettings || currentSettings[field] === undefined) {
      return fallback;
    }
    return currentSettings[field];
  };

  const handleToggleEnabled = (field) => {
    if (!currentSettings) return;
    
    const newValue = !currentSettings[field];
    dispatch(updateCurrentTemplate({ field, value: newValue }));
    setHasChanges(true);
    
    // 검증 오류 초기화 - 필드명을 알림 타입으로 변환
    const notificationType = notificationTypes.find(type => type.enabledField === field);
    if (notificationType && validationErrors[notificationType.key]) {
      const newErrors = { ...validationErrors };
      delete newErrors[notificationType.key];
      setValidationErrors(newErrors);
    }
  };

  const handleTemplateSelect = (field, templateId) => {
    dispatch(updateCurrentTemplate({ field, value: templateId }));
    setHasChanges(true);
    
    // 템플릿 선택 시 해당 알림 타입의 검증 오류 초기화
    const notificationType = notificationTypes.find(type => type.templateField === field);
    if (notificationType && validationErrors[notificationType.key]) {
      const newErrors = { ...validationErrors };
      delete newErrors[notificationType.key];
      setValidationErrors(newErrors);
    }
  };

  const handleToggleCustomSettings = () => {
    if (!currentSettings) return;
    
    const newValue = !currentSettings.use_custom_settings;
    dispatch(updateCurrentTemplate({ field: 'use_custom_settings', value: newValue }));
    setHasChanges(true);
  };

  const handleToggleSubOption = (field, typeKey = null) => {
    if (!currentSettings) return;
    
    const newValue = !currentSettings[field];
    dispatch(updateCurrentTemplate({ field, value: newValue }));
    setHasChanges(true);

    if (typeKey && validationErrors[typeKey]) {
      const newErrors = { ...validationErrors };
      delete newErrors[typeKey];
      setValidationErrors(newErrors);
    }
  };

  const handleSave = async () => {
    if (!currentSettings || !hasChanges) return;

    // 검증 실행
    const errors = validateNotificationSettings();
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return;
    }

    try {
      await dispatch(updateNotificationSettings({
        id: currentSettings.id,
        data: currentSettings
      })).unwrap();
      setHasChanges(false);
      setValidationErrors({});
    } catch (error) {
      console.error('설정 저장 실패:', error);
    }
  };

  const getSelectedTemplate = (templateField) => {
    const templateId = currentSettings?.[templateField];
    return templates.find(t => t.id === templateId || t.sendon_template_id === templateId);
  };

  const getApprovedTemplates = () => {
    return templates.filter(t => t.status === 'APPROVED' || t.status === 'approved');
  };

  const isTemplateUnderReview = (template) => {
    if (!template) return false;
    const reviewStatuses = ['PENDING', 'IN_REVIEW', 'pending', 'in_review'];
    return reviewStatuses.includes(template.status);
  };

  const validateNotificationSettings = () => {
    const errors = {};
    
    notificationTypes.forEach(type => {
      if (currentSettings[type.enabledField]) {
        const errorMessages = [];
        const selectedTemplateId = currentSettings[type.templateField];
        if (!selectedTemplateId) {
          errorMessages.push('알림이 활성화되어 있지만 템플릿이 선택되지 않았습니다.');
        } else {
          const template = getSelectedTemplate(type.templateField);
          if (!template) {
            errorMessages.push('선택된 템플릿을 찾을 수 없습니다.');
          } else if (isTemplateUnderReview(template)) {
            errorMessages.push('선택된 템플릿이 아직 검수 중입니다. 승인된 템플릿을 선택해주세요.');
          } else if (template.status !== 'APPROVED' && template.status !== 'approved') {
            errorMessages.push('승인되지 않은 템플릿입니다. 승인된 템플릿을 선택해주세요.');
          }
        }

        if (type.recipientOptions && type.recipientOptions.length > 0) {
          const activeRecipients = type.recipientOptions.filter(option => 
            getToggleValue(option.field, option.defaultValue)
          );
          if (activeRecipients.length === 0) {
            errorMessages.push('알림을 발송할 대상을 최소 1명 이상 선택해주세요.');
          }
        }

        if (errorMessages.length > 0) {
          errors[type.key] = errorMessages.join(' ');
        }
      }
    });
    
    return errors;
  };

  if (!currentSettings) {
    return (
      <div className="settings-panel">
        <div className="loading-message">
          <div className="api-setup-guide">
            <h3>센드온 API 설정이 필요합니다.</h3>
            <p>알림톡 설정을 시작하기 전에 먼저 센드온 API 연동을 완료해주세요.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-panel with-content">
      <div className="panel-header">
        <div className="header-content">
          <h3>알림 설정</h3>
          <p>각 상황별로 알림 발송 여부를 설정하고 사용할 템플릿을 선택할 수 있습니다.</p>
        </div>
        <div className="header-actions">
          <button 
            className="refresh-button"
            onClick={loadTemplates}
            disabled={loadingTemplates}
          >
            {loadingTemplates ? '센드온과 동기화 중...' : '센드온과 동기화'}
          </button>
          <button 
            className={`save-button ${hasChanges ? 'has-changes' : ''}`}
            onClick={handleSave}
            disabled={!hasChanges || loading.settings}
          >
            {loading.settings ? '저장 중...' : '설정 저장'}
          </button>
        </div>
      </div>

      {/* 레벨별 설정인 경우 커스텀 설정 토글 표시 */}
      {currentSettings?.level && (
        <div className="custom-settings-toggle">
          <div className="toggle-container">
            <div className="toggle-info">
              <h4>추가 설정 사용</h4>
              <p>이 반에서 글로벌 설정 대신 개별 설정을 사용합니다. 비활성화하면 글로벌 설정이 적용됩니다.</p>
            </div>
            <div className="setting-toggle">
              <input
                type="checkbox"
                id="toggle-custom-settings"
                checked={currentSettings.use_custom_settings || false}
                onChange={handleToggleCustomSettings}
                className="toggle-input"
              />
              <label htmlFor="toggle-custom-settings" className="toggle-label">
                <span className="toggle-switch"></span>
              </label>
            </div>
          </div>
        </div>
      )}

      {/* Global setting: Enable notification queue */}
      {!currentSettings?.level && (
        <div className="custom-settings-toggle">
          <div className="toggle-container">
            <div className="toggle-info">
              <h4>알림 대기열 사용</h4>
              <p>오후 11시 이후 발송되는 알림을 익일 오전 9시에 자동 발송합니다. 비활성화하면 모든 알림이 즉시 발송됩니다.</p>
            </div>
            <div className="setting-toggle">
              <input
                type="checkbox"
                id="toggle-enable-queue"
                checked={currentSettings?.enable_queue ?? true}
                onChange={() => {
                  const newValue = !currentSettings.enable_queue;
                  dispatch(updateCurrentTemplate({ field: 'enable_queue', value: newValue }));
                  setHasChanges(true);
                }}
                className="toggle-input"
              />
              <label htmlFor="toggle-enable-queue" className="toggle-label">
                <span className="toggle-switch"></span>
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="settings-list">
        {notificationTypes.map((type) => {
          // 레벨별 설정이고 커스텀 설정이 비활성화된 경우 비활성화
          const isDisabled = currentSettings?.level && !currentSettings?.use_custom_settings;
          const recipientOptions = type.recipientOptions || [];
          const activeRecipients = recipientOptions.filter(option =>
            getToggleValue(option.field, option.defaultValue)
          );
          const recipientBadgeText = recipientOptions.length === 0
            ? '설정 없음'
            : (activeRecipients.length > 0
                ? activeRecipients.map(option => option.audienceLabel).join(', ')
                : '선택되지 않음');
          
          return (
            <div key={type.key} className={`notification-setting ${isDisabled ? 'disabled' : ''}`}>
              <div className="setting-header">
                <div className="setting-main">
                  <div className="setting-title-area">
                    <h4 className="setting-title">{type.title}</h4>
                    <span className="recipient-badge">수신자: {recipientBadgeText}</span>
                    {isDisabled && <span className="global-setting-badge">글로벌 설정 사용 중</span>}
                  </div>
                  <div className="setting-toggle">
                    <input
                      type="checkbox"
                      id={`toggle-${type.key}`}
                      checked={currentSettings[type.enabledField] || false}
                      onChange={() => handleToggleEnabled(type.enabledField)}
                      className="toggle-input"
                      disabled={isDisabled}
                    />
                    <label htmlFor={`toggle-${type.key}`} className={`toggle-label ${isDisabled ? 'disabled' : ''}`}>
                      <span className="toggle-switch"></span>
                    </label>
                  </div>
              </div>
              <p className="setting-description">{type.description}</p>
            </div>

            {currentSettings[type.enabledField] && !isDisabled && (
              <div className="template-selection">
                <div className="template-selection-header">
                  <h5>사용할 템플릿 선택</h5>
                  {loadingTemplates && <span className="loading-text">템플릿 불러오는 중...</span>}
                </div>
                
                {templates.length === 0 ? (
                  <div className="no-templates">
                    <p>사용 가능한 템플릿이 없습니다.</p>
                    <p>먼저 "템플릿 설정" 탭에서 센드온과 동기화하거나 센드온에서 템플릿을 생성해주세요.</p>
                  </div>
                ) : (
                  <div className="template-dropdown">
                    <select
                      value={currentSettings[type.templateField] || ''}
                      onChange={(e) => handleTemplateSelect(type.templateField, e.target.value)}
                      disabled={isDisabled}
                      className="template-select"
                    >
                      <option value="">템플릿을 선택해주세요</option>
                      {templates.map(template => (
                        <option 
                          key={template.id} 
                          value={template.sendon_template_id || template.id}
                        >
                          {template.template_name} ({template.status === 'APPROVED' ? '승인완료' : 
                           template.status === 'DRAFT' ? '작성중' :
                           template.status === 'PENDING' ? '검수대기' :
                           template.status === 'IN_REVIEW' ? '검수중' : 
                           template.status || '알 수 없음'})
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
            )}

            {/* 수신자 선택 옵션 */}
            {recipientOptions.length > 0 && currentSettings[type.enabledField] && !isDisabled && (
              <div className="recipient-options">
                <div className="recipient-options-header">
                  <h5>수신자 선택</h5>
                  <span>필요한 대상에게만 알림을 보내도록 설정하세요.</span>
                </div>
                <div className="recipient-options-list">
                  {recipientOptions.map((option, idx) => (
                    <div key={option.field} className="recipient-option">
                      <div className="recipient-option-info">
                        <span className="recipient-option-title">{option.title}</span>
                        <span className="recipient-option-description">{option.description}</span>
                      </div>
                      <div className="setting-toggle">
                        <input
                          type="checkbox"
                          id={`recipient-${type.key}-${idx}`}
                          checked={getToggleValue(option.field, option.defaultValue)}
                          onChange={() => handleToggleSubOption(option.field, type.key)}
                          className="toggle-input"
                        />
                        <label htmlFor={`recipient-${type.key}-${idx}`} className="toggle-label">
                          <span className="toggle-switch"></span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 서브 옵션 (복습 과제 포함 등) */}
            {type.subOptions && type.subOptions.length > 0 && currentSettings[type.enabledField] && !isDisabled && (
              <div className="sub-options">
                {type.subOptions.map((subOption, idx) => (
                  <div key={subOption.field} className="sub-option">
                    <div className="sub-option-container">
                      <div className="sub-option-info">
                        <span className="sub-option-title">{subOption.title}</span>
                        <span className="sub-option-description">{subOption.description}</span>
                      </div>
                      <div className="setting-toggle">
                        <input
                          type="checkbox"
                          id={`sub-${type.key}-${idx}`}
                          checked={getToggleValue(subOption.field, subOption.defaultValue ?? false)}
                          onChange={() => handleToggleSubOption(subOption.field, type.key)}
                          className="toggle-input"
                        />
                        <label htmlFor={`sub-${type.key}-${idx}`} className="toggle-label">
                          <span className="toggle-switch"></span>
                        </label>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* 검증 오류 표시 */}
            {validationErrors[type.key] && (
              <div className="validation-error">
                ⚠️ <strong>설정 오류:</strong> {validationErrors[type.key]}
              </div>
            )}
          </div>
          );
        })}
      </div>


      {error.settings && (
        <div className="error-message">
          설정 저장 중 오류가 발생했습니다: {typeof error.settings === 'string' ? error.settings : JSON.stringify(error.settings)}
        </div>
      )}


      <style jsx>{`
        .settings-panel {
          width: 100%;
        }

        .settings-panel.with-content {
          padding: 20px;
        }

        .panel-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 20px;
          padding-bottom: 16px;
          border-bottom: 1px solid #e0e0e0;
        }

        .header-content h3 {
          font-size: 20px;
          margin: 0 0 4px 0;
          color: #333;
        }

        .header-content p {
          font-size: 14px;
          color: #666;
          margin: 0;
          line-height: 1.4;
        }

        .header-actions {
          display: flex;
          gap: 12px;
        }

        .refresh-button {
          padding: 8px 16px;
          border: 1px solid #17a2b8;
          border-radius: 4px;
          background: #17a2b8;
          color: white;
          cursor: pointer;
          font-size: 14px;
          transition: all 0.2s;
        }

        .refresh-button:hover:not(:disabled) {
          background: #138496;
          border-color: #138496;
        }

        .refresh-button:disabled {
          background: #ccc;
          border-color: #ccc;
          cursor: not-allowed;
        }

        .save-button {
          padding: 8px 20px;
          border: 1px solid #ddd;
          border-radius: 4px;
          background: #f8f9fa;
          color: #666;
          cursor: not-allowed;
          font-size: 14px;
          font-weight: 500;
          transition: all 0.2s;
        }

        .save-button.has-changes {
          background: #007bff;
          border-color: #007bff;
          color: white;
          cursor: pointer;
        }

        .save-button.has-changes:hover {
          background: #0056b3;
        }

        .save-button:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .custom-settings-toggle {
          background: #f8f9fa;
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 20px;
        }

        .toggle-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .toggle-info h4 {
          font-size: 16px;
          margin: 0 0 4px 0;
          color: #333;
        }

        .toggle-info p {
          font-size: 13px;
          color: #666;
          margin: 0;
          line-height: 1.4;
        }

        .settings-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .additional-settings {
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid #e0e0e0;
        }

        .additional-settings h4 {
          font-size: 18px;
          margin: 0 0 16px 0;
          color: #333;
          font-weight: 600;
        }

        .retry-setting-title {
          display: flex !important;
          align-items: center !important;
          gap: 12px !important;
        }

        .retry-setting-title .setting-title {
          margin: 0 !important;
          line-height: 1 !important;
        }

        .sub-option {
          margin-top: 12px;
          padding: 12px;
          background: #f8f9fa;
          border: 1px solid #e9ecef;
          border-radius: 6px;
        }

        .sub-option-container {
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .sub-option-info {
          flex: 1;
          display: flex;
          flex-direction: column;
          gap: 2px;
        }

        .sub-option-title {
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .sub-option-description {
          font-size: 12px;
          color: #666;
          line-height: 1.3;
        }

        .notification-setting {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 16px;
          background: #fafafa;
          transition: all 0.2s;
        }

        .notification-setting.disabled {
          background: #f5f5f5;
          border-color: #d0d0d0;
          opacity: 0.7;
        }

        .setting-header {
          margin-bottom: 12px;
        }

        .setting-main {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 6px;
        }

        .setting-title-area {
          display: flex;
          align-items: center;
          gap: 12px;
          flex: 1;
        }

        .setting-title {
          font-size: 16px;
          margin: 0;
          color: #333;
        }

        .recipient-badge {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          background: #e3f2fd;
          color: #1976d2;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
        }

        .global-setting-badge {
          display: inline-flex;
          align-items: center;
          padding: 2px 8px;
          background: #fff3cd;
          color: #856404;
          border-radius: 12px;
          font-size: 11px;
          font-weight: 500;
          white-space: nowrap;
          margin-left: 8px;
        }

        .setting-toggle {
          position: relative;
          flex-shrink: 0;
        }

        .toggle-input {
          display: none;
        }

        .toggle-label {
          display: block;
          width: 48px;
          height: 28px;
          background: #ddd;
          border-radius: 14px;
          cursor: pointer;
          transition: all 0.3s;
          position: relative;
        }

        .toggle-input:checked + .toggle-label {
          background: #007bff;
        }

        .toggle-switch {
          position: absolute;
          top: 2px;
          left: 2px;
          width: 24px;
          height: 24px;
          background: white;
          border-radius: 12px;
          transition: all 0.3s;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
        }

        .toggle-input:checked + .toggle-label .toggle-switch {
          transform: translateX(20px);
        }

        .toggle-label.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .toggle-input:disabled + .toggle-label {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .setting-description {
          font-size: 13px;
          color: #666;
          margin: 0;
          line-height: 1.4;
        }


        .template-selection {
          background: #f8f9fa;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          padding: 16px;
          margin-top: 12px;
        }

        .template-selection-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 12px;
        }

        .template-selection-header h5 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .loading-text {
          font-size: 12px;
          color: #666;
          font-style: italic;
        }

        .no-templates {
          text-align: center;
          padding: 20px;
          color: #666;
        }

        .no-templates p {
          margin: 0 0 8px 0;
          font-size: 13px;
          line-height: 1.4;
        }

        .template-dropdown {
          width: 100%;
        }
        
        .template-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 6px;
          font-size: 14px;
          background: white;
          color: #333;
          cursor: pointer;
          transition: all 0.2s;
        }
        
        .template-select:focus {
          outline: none;
          border-color: #007bff;
          box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
        }
        
        .template-select:disabled {
          background: #f5f5f5;
          color: #999;
          cursor: not-allowed;
        }

        .recipient-options {
          margin-top: 12px;
          padding: 12px 16px;
          border: 1px solid #e0e0e0;
          border-radius: 6px;
          background: #fff;
        }

        .recipient-options-header {
          display: flex;
          justify-content: space-between;
          align-items: baseline;
          gap: 12px;
          margin-bottom: 10px;
        }

        .recipient-options-header h5 {
          margin: 0;
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .recipient-options-header span {
          font-size: 12px;
          color: #666;
        }

        .recipient-options-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .recipient-option {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
        }

        .recipient-option-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .recipient-option-title {
          font-size: 14px;
          font-weight: 600;
          color: #333;
        }

        .recipient-option-description {
          font-size: 12px;
          color: #666;
          line-height: 1.4;
        }


        .validation-error {
          padding: 8px 12px;
          margin-top: 8px;
          background: #fff5f5;
          border-left: 3px solid #e53e3e;
          border-radius: 4px;
          font-size: 13px;
          color: #744210;
          line-height: 1.4;
        }

        .validation-error strong {
          color: #e53e3e;
          font-weight: 600;
        }


        .loading-message {
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100%;
          height: 100%;
          padding: 40px;
          background: linear-gradient(135deg, #f5f7ff 0%, #ffffff 100%);
        }

        .api-setup-guide {
          max-width: 400px;
          width: 100%;
          text-align: center;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          padding: 48px 32px;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
        }

        .guide-icon {
          font-size: 64px;
          margin-bottom: 24px;
          display: block;
        }

        .api-setup-guide h3 {
          color: #1e293b;
          margin: 0 0 16px 0;
          font-size: 22px;
          font-weight: 600;
        }

        .api-setup-guide p {
          color: #64748b;
          margin: 0 0 32px 0;
          line-height: 1.6;
          font-size: 15px;
        }

        .action-guide {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 20px;
        }

        .action-text {
          color: #475569;
          font-size: 16px;
          font-weight: 500;
        }

        .error-message {
          margin-top: 20px;
          padding: 12px 16px;
          background: #ffebee;
          border: 1px solid #ffcdd2;
          border-radius: 4px;
          color: #c62828;
          font-size: 14px;
        }

        @media (max-width: 768px) {
          .settings-panel {
            padding: 16px;
          }
          
          .panel-header {
            flex-direction: column;
            gap: 16px;
            align-items: stretch;
          }
          
          .header-actions {
            justify-content: stretch;
          }
          
          .header-actions button {
            flex: 1;
          }
          
          .setting-main {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          
          .setting-title-area {
            justify-content: space-between;
          }
          
          .setting-toggle {
            align-self: flex-end;
          }
          
          .editor-header {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }
          
          .macro-buttons {
            justify-content: flex-start;
          }
        }
      `}</style>
    </div>
  );
};

export default NotificationSettingsPanel;
